import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    Shield, 
    Trophy, 
    Plus, 
    Users, 
    Calendar,
    Coins,
    Settings,
    Play,
    Square,
    Award,
    Loader2,
    ExternalLink,
    RefreshCw,
    AlertTriangle,
    CheckCircle,
    Clock,
    Target,
    Zap
} from 'lucide-react';
import { TOURNAMENT_CONTRACT_ADDRESS, TOURNAMENT_CONTRACT_ABI, TOURNAMENT_STATUS, TOURNAMENT_TYPE, PRIZE_DISTRIBUTIONS } from '../contracts/tournamentConfig';
import { getExplorerUrl } from '../lib/contract';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Treasury address (contract owner)
const TREASURY_ADDRESS = "0xAf122740bD739B49d980a48Dfb6E0141cCE79338";

export function AdminPage() {
    const { address, isConnected, chainId } = useWeb3();
    const [activeTab, setActiveTab] = useState('tournaments');
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Check if user is admin (treasury owner)
    const isAdmin = address?.toLowerCase() === TREASURY_ADDRESS.toLowerCase();
    
    useEffect(() => {
        if (isAdmin) {
            fetchTournaments();
        }
    }, [isAdmin]);
    
    const fetchTournaments = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/tournaments?limit=50`);
            if (response.ok) {
                const data = await response.json();
                setTournaments(data);
            }
        } catch (error) {
            console.error('Failed to fetch tournaments:', error);
        } finally {
            setLoading(false);
        }
    };
    
    if (!isConnected) {
        return (
            <div className="max-w-5xl mx-auto px-4 py-12">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-12 text-center">
                        <Shield className="w-16 h-16 text-white/20 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-white mb-2">Admin Access Required</h2>
                        <p className="text-white/50">Connect your wallet to access the admin panel.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    if (!isAdmin) {
        return (
            <div className="max-w-5xl mx-auto px-4 py-12">
                <Card className="bg-red-500/10 border-red-500/30">
                    <CardContent className="p-12 text-center">
                        <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
                        <p className="text-white/50 mb-4">
                            This panel is restricted to platform administrators.
                        </p>
                        <p className="text-white/30 text-sm font-mono">
                            Connected: {address?.slice(0, 10)}...
                        </p>
                        <p className="text-white/30 text-sm font-mono">
                            Required: {TREASURY_ADDRESS.slice(0, 10)}...
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#9B6FE8]/20 to-[#6F3FCA]/20 flex items-center justify-center">
                        <Shield className="w-7 h-7 text-[#9B6FE8]" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
                        <p className="text-white/50 text-sm">Manage tournaments, teams & seasons</p>
                    </div>
                </div>
                <Badge className="bg-[#00FF66]/20 text-[#00FF66]">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Admin Access
                </Badge>
            </div>

            {/* Contract Info */}
            <Card className="bg-white/[0.02] border-white/5 mb-6">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Zap className="w-5 h-5 text-[#FFD700]" />
                            <div>
                                <div className="text-sm text-white">Tournament Contract</div>
                                <div className="text-xs text-white/40 font-mono">{TOURNAMENT_CONTRACT_ADDRESS}</div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="border-[#00FF66]/30 text-[#00FF66] hover:bg-[#00FF66]/10"
                                data-testid="sync-scores-btn"
                                onClick={async () => {
                                    try {
                                        toast.info('Syncing on-chain scores...');
                                        const res = await fetch(`${API_URL}/api/tournaments/sync-all-scores`, { method: 'POST' });
                                        if (res.ok) {
                                            const data = await res.json();
                                            toast.success(`Scores synced: ${data.synced || 0} tournaments`);
                                            fetchTournaments();
                                        } else {
                                            toast.error('Sync failed');
                                        }
                                    } catch (e) {
                                        toast.error('Sync error: ' + e.message);
                                    }
                                }}
                            >
                                <Target className="w-4 h-4 mr-1" />
                                Sync Scores
                            </Button>
                            <a href={getExplorerUrl(chainId || 8453, 'address', TOURNAMENT_CONTRACT_ADDRESS)} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="border-white/10">
                                    <ExternalLink className="w-4 h-4" />
                                </Button>
                            </a>
                            <Button variant="outline" size="sm" className="border-white/10" onClick={fetchTournaments}>
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-white/5 border border-white/10 mb-6">
                    <TabsTrigger 
                        value="tournaments"
                        className="data-[state=active]:bg-[#9B6FE8]/10 data-[state=active]:text-[#9B6FE8]"
                    >
                        <Trophy className="w-4 h-4 mr-2" />
                        Tournaments
                    </TabsTrigger>
                    <TabsTrigger 
                        value="create"
                        className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Tournament
                    </TabsTrigger>
                    <TabsTrigger 
                        value="seasons"
                        className="data-[state=active]:bg-[#FFD700]/10 data-[state=active]:text-[#FFD700]"
                    >
                        <Calendar className="w-4 h-4 mr-2" />
                        Seasons
                    </TabsTrigger>
                    <TabsTrigger 
                        value="analytics"
                        className="data-[state=active]:bg-[#FF6B6B]/10 data-[state=active]:text-[#FF6B6B]"
                    >
                        <Target className="w-4 h-4 mr-2" />
                        Analytics
                    </TabsTrigger>
                </TabsList>

                {/* Tournaments Tab */}
                <TabsContent value="tournaments">
                    <TournamentManagement tournaments={tournaments} onRefresh={fetchTournaments} loading={loading} />
                </TabsContent>

                {/* Create Tournament Tab */}
                <TabsContent value="create">
                    <CreateTournamentForm onSuccess={fetchTournaments} />
                </TabsContent>

                {/* Seasons Tab */}
                <TabsContent value="seasons">
                    <SeasonManagement />
                </TabsContent>

                {/* Analytics Tab */}
                <TabsContent value="analytics">
                    <TournamentAnalytics tournaments={tournaments} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// Tournament Management Component
function TournamentManagement({ tournaments, onRefresh, loading }) {
    const { chainId } = useWeb3();
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [showEndModal, setShowEndModal] = useState(false);
    const [winners, setWinners] = useState(['', '', '']);
    
    // Contract writes
    const { writeContract: startTournament, data: startHash, isPending: isStartPending } = useWriteContract();
    const { writeContract: endTournamentContract, data: endHash, isPending: isEndPending } = useWriteContract();
    const { writeContract: cancelTournament, data: cancelHash, isPending: isCancelPending } = useWriteContract();
    
    const { isSuccess: startSuccess } = useWaitForTransactionReceipt({ hash: startHash });
    const { isSuccess: endSuccess } = useWaitForTransactionReceipt({ hash: endHash });
    const { isSuccess: cancelSuccess } = useWaitForTransactionReceipt({ hash: cancelHash });
    
    useEffect(() => {
        if (startSuccess || endSuccess || cancelSuccess) {
            toast.success('Transaction confirmed!');
            setShowEndModal(false);
            setSelectedTournament(null);
            setWinners(['', '', '']);
            onRefresh();
        }
    }, [startSuccess, endSuccess, cancelSuccess, onRefresh]);
    
    const handleStart = async (tournament) => {
        if (!tournament.on_chain_id) {
            toast.error('Tournament not on-chain');
            return;
        }
        try {
            await startTournament({
                address: TOURNAMENT_CONTRACT_ADDRESS,
                abi: TOURNAMENT_CONTRACT_ABI,
                functionName: 'startTournament',
                args: [BigInt(tournament.on_chain_id)]
            });
        } catch (error) {
            toast.error('Failed to start tournament');
        }
    };
    
    const handleEndTournament = async () => {
        if (!selectedTournament?.on_chain_id) {
            toast.error('Tournament not on-chain');
            return;
        }
        
        // Filter valid winner addresses
        const validWinners = winners.filter(w => w && w.startsWith('0x') && w.length === 42);
        if (validWinners.length === 0) {
            toast.error('Enter at least one valid winner address');
            return;
        }
        
        try {
            await endTournamentContract({
                address: TOURNAMENT_CONTRACT_ADDRESS,
                abi: TOURNAMENT_CONTRACT_ABI,
                functionName: 'endTournament',
                args: [BigInt(selectedTournament.on_chain_id), validWinners]
            });
            toast.info('Ending tournament and distributing prizes...');
        } catch (error) {
            toast.error('Failed to end tournament');
        }
    };
    
    const handleCancel = async (tournament) => {
        if (!tournament.on_chain_id) {
            toast.error('Tournament not on-chain');
            return;
        }
        try {
            await cancelTournament({
                address: TOURNAMENT_CONTRACT_ADDRESS,
                abi: TOURNAMENT_CONTRACT_ABI,
                functionName: 'cancelTournament',
                args: [BigInt(tournament.on_chain_id)]
            });
        } catch (error) {
            toast.error('Failed to cancel tournament');
        }
    };
    
    const openEndModal = (tournament) => {
        setSelectedTournament(tournament);
        // Pre-fill winners from leaderboard if available
        const leaderboard = tournament.leaderboard || [];
        setWinners([
            leaderboard[0]?.address || '',
            leaderboard[1]?.address || '',
            leaderboard[2]?.address || ''
        ]);
        setShowEndModal(true);
    };
    
    const statusColors = {
        active: 'bg-[#00FF66]/20 text-[#00FF66]',
        upcoming: 'bg-[#FFD700]/20 text-[#FFD700]',
        completed: 'bg-[#9B6FE8]/20 text-[#9B6FE8]',
        cancelled: 'bg-red-500/20 text-red-400'
    };

    if (loading) {
        return (
            <div className="space-y-4">
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-24 w-full bg-white/5" />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">All Tournaments ({tournaments.length})</h3>
                <Button variant="outline" size="sm" onClick={onRefresh} className="border-white/10">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>
            
            {tournaments.length === 0 ? (
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-8 text-center">
                        <Trophy className="w-12 h-12 text-white/20 mx-auto mb-4" />
                        <p className="text-white/50">No tournaments yet</p>
                    </CardContent>
                </Card>
            ) : (
                tournaments.map(tournament => (
                    <Card key={tournament.id} className="bg-white/[0.02] border-white/5 hover:border-white/10 transition-colors" data-testid={`tournament-${tournament.id}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h4 className="text-white font-semibold">{tournament.name}</h4>
                                        <Badge className={statusColors[tournament.status] || statusColors.upcoming}>
                                            {tournament.status}
                                        </Badge>
                                        {tournament.on_chain_id && (
                                            <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-xs">
                                                <Zap className="w-3 h-3 mr-1" />
                                                On-chain #{tournament.on_chain_id}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-white/50 text-sm mb-2">{tournament.description}</p>
                                    <div className="flex items-center gap-4 text-xs text-white/40">
                                        <span className="flex items-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {tournament.current_participants || 0} / {tournament.max_participants || '∞'}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Coins className="w-3 h-3" />
                                            {tournament.prize_pool || 0} ETH
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {new Date(tournament.end_date).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {tournament.status === 'upcoming' && tournament.on_chain_id && (
                                        <Button 
                                            size="sm" 
                                            className="bg-[#00FF66] text-[#0A0A0C]"
                                            onClick={() => handleStart(tournament)}
                                            disabled={isStartPending}
                                            data-testid={`start-tournament-${tournament.id}`}
                                        >
                                            {isStartPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                                        </Button>
                                    )}
                                    {tournament.status === 'active' && tournament.on_chain_id && (
                                        <Button 
                                            size="sm" 
                                            className="bg-[#9B6FE8] text-white"
                                            onClick={() => openEndModal(tournament)}
                                            data-testid={`end-tournament-${tournament.id}`}
                                        >
                                            <Award className="w-4 h-4" />
                                        </Button>
                                    )}
                                    {(tournament.status === 'upcoming' || tournament.status === 'active') && tournament.on_chain_id && (
                                        <Button 
                                            size="sm" 
                                            variant="destructive"
                                            onClick={() => handleCancel(tournament)}
                                            disabled={isCancelPending}
                                            data-testid={`cancel-tournament-${tournament.id}`}
                                        >
                                            {isCancelPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))
            )}
            
            {/* End Tournament Modal */}
            {showEndModal && selectedTournament && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="bg-[#1A0B2E] border-[#9B6FE8]/30 max-w-lg w-full">
                        <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                                <Award className="w-5 h-5 text-[#FFD700]" />
                                End Tournament & Distribute Prizes
                            </CardTitle>
                            <CardDescription className="text-white/50">
                                {selectedTournament.name} - Prize Pool: {selectedTournament.prize_pool || 0} ETH
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Alert className="bg-[#FFD700]/10 border-[#FFD700]/30">
                                <AlertTriangle className="w-4 h-4 text-[#FFD700]" />
                                <AlertDescription className="text-white/80">
                                    This action is irreversible. Prizes will be distributed immediately on-chain.
                                </AlertDescription>
                            </Alert>
                            
                            <div className="space-y-3">
                                <label className="text-sm text-white/60">Enter Winner Addresses (in order)</label>
                                {[0, 1, 2].map(idx => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <span className="text-[#FFD700] font-bold w-8">#{idx + 1}</span>
                                        <Input
                                            value={winners[idx]}
                                            onChange={e => {
                                                const newWinners = [...winners];
                                                newWinners[idx] = e.target.value;
                                                setWinners(newWinners);
                                            }}
                                            placeholder="0x..."
                                            className="bg-white/5 border-white/10 font-mono text-sm"
                                            data-testid={`winner-input-${idx}`}
                                        />
                                    </div>
                                ))}
                                <p className="text-xs text-white/40">
                                    Prize distribution: 50% / 30% / 20% (based on contract settings)
                                </p>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button 
                                    variant="outline" 
                                    className="flex-1 border-white/10"
                                    onClick={() => {
                                        setShowEndModal(false);
                                        setSelectedTournament(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    className="flex-1 bg-[#00FF66] text-[#0A0A0C]"
                                    onClick={handleEndTournament}
                                    disabled={isEndPending}
                                    data-testid="confirm-end-tournament"
                                >
                                    {isEndPending ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                                    ) : (
                                        <><Trophy className="w-4 h-4 mr-2" />End & Distribute</>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

// Create Tournament Form
function CreateTournamentForm({ onSuccess }) {
    const { chainId } = useWeb3();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'win_streak',
        entryFee: '0',
        prizePool: '0',
        maxParticipants: '50',
        minParticipants: '2',
        startDate: '',
        endDate: '',
        isTeamTournament: false,
        teamSize: '2',
        prizeDistribution: 'TOP_3'
    });
    const [createOnChain, setCreateOnChain] = useState(false);
    const [creating, setCreating] = useState(false);
    
    // Contract write
    const { 
        writeContract: createTournamentOnChain, 
        data: createHash,
        isPending: isCreatePending,
        error: createError
    } = useWriteContract();
    
    const { isLoading: isCreateConfirming, isSuccess: isCreateSuccess } = 
        useWaitForTransactionReceipt({ hash: createHash });
    
    useEffect(() => {
        if (isCreateSuccess) {
            toast.success('Tournament created on-chain!');
            onSuccess();
            resetForm();
        }
    }, [isCreateSuccess, onSuccess]);
    
    useEffect(() => {
        if (createError) {
            toast.error('Failed to create on-chain tournament');
            setCreating(false);
        }
    }, [createError]);
    
    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            type: 'win_streak',
            entryFee: '0',
            prizePool: '0',
            maxParticipants: '50',
            minParticipants: '2',
            startDate: '',
            endDate: '',
            isTeamTournament: false,
            teamSize: '2',
            prizeDistribution: 'TOP_3'
        });
        setCreating(false);
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        
        // Validate
        if (!formData.name || !formData.startDate || !formData.endDate) {
            toast.error('Please fill all required fields');
            setCreating(false);
            return;
        }
        
        const startTime = Math.floor(new Date(formData.startDate).getTime() / 1000);
        const endTime = Math.floor(new Date(formData.endDate).getTime() / 1000);
        
        if (startTime <= Math.floor(Date.now() / 1000)) {
            toast.error('Start date must be in the future');
            setCreating(false);
            return;
        }
        
        if (endTime <= startTime) {
            toast.error('End date must be after start date');
            setCreating(false);
            return;
        }
        
        if (createOnChain) {
            // Create on-chain
            const typeMap = { win_streak: 0, volume: 1, profit: 2, oracle: 3, custom: 4 };
            const distribution = PRIZE_DISTRIBUTIONS[formData.prizeDistribution];
            
            try {
                await createTournamentOnChain({
                    address: TOURNAMENT_CONTRACT_ADDRESS,
                    abi: TOURNAMENT_CONTRACT_ABI,
                    functionName: 'createTournament',
                    args: [
                        formData.name,
                        typeMap[formData.type] || 0,
                        parseEther(formData.entryFee || '0'),
                        BigInt(formData.maxParticipants || 50),
                        BigInt(formData.minParticipants || 2),
                        BigInt(startTime),
                        BigInt(endTime),
                        formData.isTeamTournament,
                        BigInt(formData.teamSize || 0),
                        distribution.map(d => BigInt(d))
                    ]
                });
            } catch (error) {
                console.error('Create error:', error);
                setCreating(false);
            }
        } else {
            // Create via API
            try {
                const response = await fetch(
                    `${API_URL}/api/tournaments?name=${encodeURIComponent(formData.name)}&description=${encodeURIComponent(formData.description)}&tournament_type=${formData.type}&start_date=${new Date(formData.startDate).toISOString()}&end_date=${new Date(formData.endDate).toISOString()}&prize_pool=${formData.prizePool}&max_participants=${formData.maxParticipants}&min_bets=${formData.minParticipants}`,
                    { method: 'POST' }
                );
                
                if (response.ok) {
                    toast.success('Tournament created!');
                    onSuccess();
                    resetForm();
                } else {
                    const data = await response.json();
                    toast.error(data.error || 'Failed to create tournament');
                }
            } catch (error) {
                toast.error('Failed to create tournament');
            } finally {
                setCreating(false);
            }
        }
    };

    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Plus className="w-5 h-5 text-[#00FF66]" />
                    Create New Tournament
                </CardTitle>
                <CardDescription className="text-white/50">
                    Configure and launch a new tournament
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Tournament Name *</label>
                            <Input
                                value={formData.name}
                                onChange={e => setFormData({...formData, name: e.target.value})}
                                placeholder="Weekly Win Streak"
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Tournament Type</label>
                            <Select value={formData.type} onValueChange={v => setFormData({...formData, type: v})}>
                                <SelectTrigger className="bg-white/5 border-white/10">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="win_streak">Win Streak (Most Wins)</SelectItem>
                                    <SelectItem value="volume">Volume (Highest Stakes)</SelectItem>
                                    <SelectItem value="profit">Profit (Best Returns)</SelectItem>
                                    <SelectItem value="oracle">Oracle (Best Predictions)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <div>
                        <label className="text-sm text-white/60 mb-2 block">Description</label>
                        <Input
                            value={formData.description}
                            onChange={e => setFormData({...formData, description: e.target.value})}
                            placeholder="Win the most bets to claim the prize!"
                            className="bg-white/5 border-white/10"
                        />
                    </div>
                    
                    {/* Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Start Date *</label>
                            <Input
                                type="datetime-local"
                                value={formData.startDate}
                                onChange={e => setFormData({...formData, startDate: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">End Date *</label>
                            <Input
                                type="datetime-local"
                                value={formData.endDate}
                                onChange={e => setFormData({...formData, endDate: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                    </div>
                    
                    {/* Participants & Prize */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Entry Fee (ETH)</label>
                            <Input
                                type="number"
                                step="0.001"
                                value={formData.entryFee}
                                onChange={e => setFormData({...formData, entryFee: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Max Participants</label>
                            <Input
                                type="number"
                                value={formData.maxParticipants}
                                onChange={e => setFormData({...formData, maxParticipants: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Min Participants</label>
                            <Input
                                type="number"
                                value={formData.minParticipants}
                                onChange={e => setFormData({...formData, minParticipants: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                    </div>
                    
                    {/* Prize Distribution */}
                    <div>
                        <label className="text-sm text-white/60 mb-2 block">Prize Distribution</label>
                        <Select value={formData.prizeDistribution} onValueChange={v => setFormData({...formData, prizeDistribution: v})}>
                            <SelectTrigger className="bg-white/5 border-white/10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="TOP_3">Top 3 (50% / 30% / 20%)</SelectItem>
                                <SelectItem value="TOP_5">Top 5 (40% / 25% / 15% / 10% / 10%)</SelectItem>
                                <SelectItem value="WINNER_TAKES_ALL">Winner Takes All (100%)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    {/* On-chain Toggle */}
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/20">
                        <input
                            type="checkbox"
                            checked={createOnChain}
                            onChange={e => setCreateOnChain(e.target.checked)}
                            className="w-5 h-5"
                        />
                        <div>
                            <div className="text-white font-semibold flex items-center gap-2">
                                <Zap className="w-4 h-4 text-[#FFD700]" />
                                Create On-Chain Tournament
                            </div>
                            <p className="text-white/50 text-sm">
                                Deploy to smart contract for trustless prize pools (requires gas)
                            </p>
                        </div>
                    </div>
                    
                    <Button 
                        type="submit" 
                        className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]"
                        disabled={creating || isCreatePending || isCreateConfirming}
                    >
                        {creating || isCreatePending || isCreateConfirming ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {isCreateConfirming ? 'Confirming...' : 'Creating...'}
                            </>
                        ) : (
                            <><Plus className="w-4 h-4 mr-2" />Create Tournament</>
                        )}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}

// Season Management
function SeasonManagement() {
    const [seasons, setSeasons] = useState([]);
    const [newSeason, setNewSeason] = useState({ name: '', startDate: '', endDate: '' });
    const [creating, setCreating] = useState(false);
    
    const { writeContract: createSeason, isPending: isSeasonPending } = useWriteContract();
    
    const handleCreateSeason = async () => {
        if (!newSeason.name || !newSeason.startDate || !newSeason.endDate) {
            toast.error('Please fill all fields');
            return;
        }
        
        setCreating(true);
        try {
            const startTime = Math.floor(new Date(newSeason.startDate).getTime() / 1000);
            const endTime = Math.floor(new Date(newSeason.endDate).getTime() / 1000);
            
            await createSeason({
                address: TOURNAMENT_CONTRACT_ADDRESS,
                abi: TOURNAMENT_CONTRACT_ABI,
                functionName: 'createSeason',
                args: [newSeason.name, BigInt(startTime), BigInt(endTime)]
            });
            
            toast.success('Season creation submitted!');
            setNewSeason({ name: '', startDate: '', endDate: '' });
        } catch (error) {
            toast.error('Failed to create season');
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Create Season */}
            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-[#FFD700]" />
                        Create New Season
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Season Name</label>
                            <Input
                                value={newSeason.name}
                                onChange={e => setNewSeason({...newSeason, name: e.target.value})}
                                placeholder="Season 1 - 2026"
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">Start Date</label>
                            <Input
                                type="datetime-local"
                                value={newSeason.startDate}
                                onChange={e => setNewSeason({...newSeason, startDate: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-white/60 mb-2 block">End Date</label>
                            <Input
                                type="datetime-local"
                                value={newSeason.endDate}
                                onChange={e => setNewSeason({...newSeason, endDate: e.target.value})}
                                className="bg-white/5 border-white/10"
                            />
                        </div>
                    </div>
                    <Button 
                        onClick={handleCreateSeason}
                        className="bg-[#FFD700] text-[#0A0A0C]"
                        disabled={creating || isSeasonPending}
                    >
                        {creating || isSeasonPending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Plus className="w-4 h-4 mr-2" />
                        )}
                        Create Season (On-Chain)
                    </Button>
                </CardContent>
            </Card>
            
            {/* Season Info */}
            <Card className="bg-white/[0.02] border-white/5">
                <CardContent className="p-8 text-center">
                    <Award className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">Season management coming soon</p>
                    <p className="text-white/30 text-sm">Add tournaments to seasons and track seasonal points</p>
                </CardContent>
            </Card>
        </div>
    );
}

// Tournament Analytics
function TournamentAnalytics({ tournaments }) {
    const totalPrizePool = tournaments.reduce((sum, t) => sum + (t.prize_pool || 0), 0);
    const totalParticipants = tournaments.reduce((sum, t) => sum + (t.current_participants || 0), 0);
    const activeTournaments = tournaments.filter(t => t.status === 'active').length;
    const completedTournaments = tournaments.filter(t => t.status === 'completed').length;
    
    return (
        <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Trophy className="w-6 h-6 text-[#FFD700] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">{tournaments.length}</div>
                        <div className="text-xs text-white/40">Total Tournaments</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Play className="w-6 h-6 text-[#00FF66] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">{activeTournaments}</div>
                        <div className="text-xs text-white/40">Active Now</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Coins className="w-6 h-6 text-[#9B6FE8] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">{totalPrizePool.toFixed(2)}</div>
                        <div className="text-xs text-white/40">ETH in Prizes</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Users className="w-6 h-6 text-[#FF6B6B] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">{totalParticipants}</div>
                        <div className="text-xs text-white/40">Total Participants</div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Tournament History */}
            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <CardTitle className="text-lg text-white">Tournament History</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {tournaments.slice(0, 10).map(t => (
                            <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02]">
                                <div>
                                    <div className="text-white font-medium">{t.name}</div>
                                    <div className="text-white/40 text-xs">{t.type} • {t.status}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[#FFD700] font-mono">{t.prize_pool || 0} ETH</div>
                                    <div className="text-white/40 text-xs">{t.current_participants || 0} players</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
